// Equipment Enhancement System (+1 to +15)
// Inspired by MU Online's addictive gear upgrading with visual feedback
// Glowing weapon effects, success/failure rates, and stat scaling

import * as THREE from 'three';
import type { ItemId } from 'shared';

export interface EnhancementResult {
  success: boolean;
  newLevel: number;
  destroyed: boolean;
  message: string;
}

export interface EnhancedItem {
  itemId: ItemId;
  enhancementLevel: number;
  bonusStats: {
    damage?: number;
    defense?: number;
    health?: number;
    mana?: number;
    critChance?: number;
    attackSpeed?: number;
  };
  glowColor?: number;
  glowIntensity?: number;
}

// Success rates per enhancement level (MU Online inspired)
const ENHANCEMENT_SUCCESS_RATES: Record<number, number> = {
  0: 1.00,   // +0 -> +1: 100%
  1: 0.95,   // +1 -> +2: 95%
  2: 0.90,   // +2 -> +3: 90%
  3: 0.85,   // +3 -> +4: 85%
  4: 0.75,   // +4 -> +5: 75%
  5: 0.65,   // +5 -> +6: 65%
  6: 0.55,   // +6 -> +7: 55%
  7: 0.45,   // +7 -> +8: 45%
  8: 0.35,   // +8 -> +9: 35%
  9: 0.30,   // +9 -> +10: 30%
  10: 0.25,  // +10 -> +11: 25%
  11: 0.20,  // +11 -> +12: 20%
  12: 0.15,  // +12 -> +13: 15%
  13: 0.10,  // +13 -> +14: 10%
  14: 0.05,  // +14 -> +15: 5%
};

// Item destruction on failure (after +6)
const DESTRUCTION_THRESHOLD = 6;
const DESTRUCTION_CHANCE = 0.25; // 25% chance to destroy on failure after threshold

// Stat bonuses per enhancement level (multiplier)
const STAT_MULTIPLIERS: Record<number, number> = {
  0: 1.00,
  1: 1.05,
  2: 1.10,
  3: 1.15,
  4: 1.22,
  5: 1.30,  // First glow tier
  6: 1.40,
  7: 1.52,
  8: 1.65,
  9: 1.80,  // Second glow tier
  10: 2.00,
  11: 2.25,
  12: 2.55, // Third glow tier
  13: 2.90,
  14: 3.30,
  15: 4.00, // Max glow tier
};

// Glow colors per tier
const GLOW_TIERS: { level: number; color: number; intensity: number }[] = [
  { level: 5, color: 0x4444ff, intensity: 0.3 },    // Blue glow at +5
  { level: 9, color: 0xff44ff, intensity: 0.5 },    // Purple glow at +9
  { level: 12, color: 0xff4444, intensity: 0.7 },   // Red glow at +12
  { level: 15, color: 0xffdd00, intensity: 1.0 },   // Gold glow at +15
];

export interface EnhancementStone {
  id: string;
  name: string;
  tier: 'common' | 'blessed' | 'divine';
  successBonus: number;      // Added to base success rate
  protectsFromDestruction: boolean;
}

const ENHANCEMENT_STONES: EnhancementStone[] = [
  {
    id: 'enhance_stone_common',
    name: 'Enhancement Stone',
    tier: 'common',
    successBonus: 0,
    protectsFromDestruction: false,
  },
  {
    id: 'enhance_stone_blessed',
    name: 'Blessed Enhancement Stone',
    tier: 'blessed',
    successBonus: 0.1,
    protectsFromDestruction: false,
  },
  {
    id: 'enhance_stone_divine',
    name: 'Divine Enhancement Stone',
    tier: 'divine',
    successBonus: 0.15,
    protectsFromDestruction: true,
  },
];

export class EnhancementSystem {
  private enhancedItems: Map<string, EnhancedItem> = new Map();

  // Callbacks
  public onEnhancementAttempt?: (result: EnhancementResult) => void;
  public onGlowUpdate?: (itemId: string, color: number, intensity: number) => void;

  constructor() {}

  public getEnhancedItem(instanceId: string): EnhancedItem | undefined {
    return this.enhancedItems.get(instanceId);
  }

  public enhance(
    instanceId: string,
    itemId: ItemId,
    baseStats: { damage?: number; defense?: number; health?: number; mana?: number },
    stoneId?: string
  ): EnhancementResult {
    let item = this.enhancedItems.get(instanceId);

    if (!item) {
      item = {
        itemId,
        enhancementLevel: 0,
        bonusStats: {},
      };
      this.enhancedItems.set(instanceId, item);
    }

    if (item.enhancementLevel >= 15) {
      return {
        success: false,
        newLevel: 15,
        destroyed: false,
        message: 'Item is already at maximum enhancement (+15)',
      };
    }

    // Calculate success rate
    let successRate = ENHANCEMENT_SUCCESS_RATES[item.enhancementLevel] ?? 0.05;

    // Apply stone bonus
    const stone = stoneId ? ENHANCEMENT_STONES.find(s => s.id === stoneId) : ENHANCEMENT_STONES[0];
    if (stone) {
      successRate = Math.min(1.0, successRate + stone.successBonus);
    }

    // Roll for success
    const roll = Math.random();
    const success = roll < successRate;

    if (success) {
      item.enhancementLevel++;

      // Calculate new stats
      const multiplier = STAT_MULTIPLIERS[item.enhancementLevel] ?? 1;
      item.bonusStats = {
        damage: baseStats.damage ? Math.floor(baseStats.damage * (multiplier - 1)) : undefined,
        defense: baseStats.defense ? Math.floor(baseStats.defense * (multiplier - 1)) : undefined,
        health: baseStats.health ? Math.floor(baseStats.health * (multiplier - 1)) : undefined,
        mana: baseStats.mana ? Math.floor(baseStats.mana * (multiplier - 1)) : undefined,
      };

      // Update glow effects
      this.updateGlowEffect(item);

      this.onEnhancementAttempt?.({
        success: true,
        newLevel: item.enhancementLevel,
        destroyed: false,
        message: `Enhancement successful! Item is now +${item.enhancementLevel}`,
      });

      return {
        success: true,
        newLevel: item.enhancementLevel,
        destroyed: false,
        message: `Enhancement successful! Item is now +${item.enhancementLevel}`,
      };
    } else {
      // Enhancement failed
      let destroyed = false;
      let message = `Enhancement failed. Item remains at +${item.enhancementLevel}`;

      // Check for destruction
      if (
        item.enhancementLevel >= DESTRUCTION_THRESHOLD &&
        !(stone?.protectsFromDestruction)
      ) {
        if (Math.random() < DESTRUCTION_CHANCE) {
          destroyed = true;
          this.enhancedItems.delete(instanceId);
          message = 'Enhancement failed! The item was destroyed!';
        } else {
          // Downgrade by 1 level on failure (after +6)
          item.enhancementLevel = Math.max(0, item.enhancementLevel - 1);
          this.updateGlowEffect(item);
          message = `Enhancement failed! Item downgraded to +${item.enhancementLevel}`;
        }
      }

      this.onEnhancementAttempt?.({
        success: false,
        newLevel: destroyed ? 0 : item.enhancementLevel,
        destroyed,
        message,
      });

      return {
        success: false,
        newLevel: destroyed ? 0 : item.enhancementLevel,
        destroyed,
        message,
      };
    }
  }

  private updateGlowEffect(item: EnhancedItem): void {
    // Find appropriate glow tier
    const glowTier = [...GLOW_TIERS]
      .reverse()
      .find(tier => item.enhancementLevel >= tier.level);

    if (glowTier) {
      item.glowColor = glowTier.color;
      item.glowIntensity = glowTier.intensity;
      this.onGlowUpdate?.(item.itemId, glowTier.color, glowTier.intensity);
    } else {
      item.glowColor = undefined;
      item.glowIntensity = undefined;
    }
  }

  public getSuccessRate(currentLevel: number, stoneId?: string): number {
    let rate = ENHANCEMENT_SUCCESS_RATES[currentLevel] ?? 0.05;
    const stone = stoneId ? ENHANCEMENT_STONES.find(s => s.id === stoneId) : undefined;
    if (stone) {
      rate = Math.min(1.0, rate + stone.successBonus);
    }
    return rate;
  }

  public getStatMultiplier(level: number): number {
    return STAT_MULTIPLIERS[level] ?? 1;
  }

  public getEnhancementStones(): EnhancementStone[] {
    return [...ENHANCEMENT_STONES];
  }

  public formatEnhancementName(baseName: string, level: number): string {
    if (level === 0) return baseName;
    return `${baseName} +${level}`;
  }

  public getGlowColor(level: number): number | undefined {
    const tier = [...GLOW_TIERS].reverse().find(t => level >= t.level);
    return tier?.color;
  }

  public getGlowIntensity(level: number): number {
    const tier = [...GLOW_TIERS].reverse().find(t => level >= t.level);
    return tier?.intensity ?? 0;
  }

  // Create glow effect material for enhanced items
  public createGlowMaterial(color: number, intensity: number): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        glowColor: { value: new THREE.Color(color) },
        intensity: { value: intensity },
        time: { value: 0 },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vViewPosition;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          vViewPosition = -mvPosition.xyz;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 glowColor;
        uniform float intensity;
        uniform float time;
        varying vec3 vNormal;
        varying vec3 vViewPosition;

        void main() {
          float rim = 1.0 - max(0.0, dot(normalize(vViewPosition), vNormal));
          rim = pow(rim, 2.0);

          // Pulsing effect
          float pulse = 0.8 + 0.2 * sin(time * 3.0);

          vec3 glow = glowColor * rim * intensity * pulse;
          gl_FragColor = vec4(glow, rim * intensity * 0.8);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
      depthWrite: false,
    });
  }

  // Create glow mesh to wrap around an item
  public createGlowMesh(
    geometry: THREE.BufferGeometry,
    level: number
  ): THREE.Mesh | null {
    const color = this.getGlowColor(level);
    const intensity = this.getGlowIntensity(level);

    if (!color || intensity === 0) return null;

    const glowGeometry = geometry.clone();
    // Scale slightly larger for glow effect
    glowGeometry.scale(1.1, 1.1, 1.1);

    const material = this.createGlowMaterial(color, intensity);
    return new THREE.Mesh(glowGeometry, material);
  }

  // Update glow animation (call each frame)
  public updateGlowMaterials(meshes: THREE.Mesh[], time: number): void {
    for (const mesh of meshes) {
      const material = mesh.material as THREE.ShaderMaterial;
      if (material.uniforms?.time) {
        material.uniforms.time.value = time;
      }
    }
  }
}

// Socket System for gems (Lineage II inspired)
export interface GemSocket {
  type: 'attack' | 'defense' | 'utility';
  gem?: Gem;
}

export interface Gem {
  id: string;
  name: string;
  type: 'attack' | 'defense' | 'utility';
  tier: 1 | 2 | 3 | 4 | 5;
  stats: {
    damage?: number;
    critChance?: number;
    defense?: number;
    health?: number;
    mana?: number;
    speed?: number;
  };
  color: number;
}

const GEM_DEFINITIONS: Gem[] = [
  // Attack gems
  { id: 'ruby_1', name: 'Chipped Ruby', type: 'attack', tier: 1, stats: { damage: 5 }, color: 0xff0000 },
  { id: 'ruby_2', name: 'Ruby', type: 'attack', tier: 2, stats: { damage: 12 }, color: 0xff0000 },
  { id: 'ruby_3', name: 'Flawless Ruby', type: 'attack', tier: 3, stats: { damage: 25 }, color: 0xff0000 },
  { id: 'ruby_4', name: 'Perfect Ruby', type: 'attack', tier: 4, stats: { damage: 45 }, color: 0xff0000 },
  { id: 'ruby_5', name: 'Radiant Ruby', type: 'attack', tier: 5, stats: { damage: 80, critChance: 5 }, color: 0xff0000 },

  // Defense gems
  { id: 'sapphire_1', name: 'Chipped Sapphire', type: 'defense', tier: 1, stats: { defense: 5 }, color: 0x0000ff },
  { id: 'sapphire_2', name: 'Sapphire', type: 'defense', tier: 2, stats: { defense: 12 }, color: 0x0000ff },
  { id: 'sapphire_3', name: 'Flawless Sapphire', type: 'defense', tier: 3, stats: { defense: 25 }, color: 0x0000ff },
  { id: 'sapphire_4', name: 'Perfect Sapphire', type: 'defense', tier: 4, stats: { defense: 45, health: 50 }, color: 0x0000ff },
  { id: 'sapphire_5', name: 'Radiant Sapphire', type: 'defense', tier: 5, stats: { defense: 80, health: 150 }, color: 0x0000ff },

  // Utility gems
  { id: 'emerald_1', name: 'Chipped Emerald', type: 'utility', tier: 1, stats: { mana: 10 }, color: 0x00ff00 },
  { id: 'emerald_2', name: 'Emerald', type: 'utility', tier: 2, stats: { mana: 25 }, color: 0x00ff00 },
  { id: 'emerald_3', name: 'Flawless Emerald', type: 'utility', tier: 3, stats: { mana: 50, speed: 2 }, color: 0x00ff00 },
  { id: 'emerald_4', name: 'Perfect Emerald', type: 'utility', tier: 4, stats: { mana: 100, speed: 4 }, color: 0x00ff00 },
  { id: 'emerald_5', name: 'Radiant Emerald', type: 'utility', tier: 5, stats: { mana: 200, speed: 8 }, color: 0x00ff00 },
];

export class SocketSystem {
  private itemSockets: Map<string, GemSocket[]> = new Map();

  public addSockets(instanceId: string, sockets: GemSocket[]): void {
    this.itemSockets.set(instanceId, sockets);
  }

  public getSockets(instanceId: string): GemSocket[] | undefined {
    return this.itemSockets.get(instanceId);
  }

  public insertGem(instanceId: string, socketIndex: number, gem: Gem): boolean {
    const sockets = this.itemSockets.get(instanceId);
    if (!sockets || socketIndex >= sockets.length) return false;

    const socket = sockets[socketIndex];
    if (socket.gem) return false; // Socket already occupied
    if (socket.type !== gem.type) return false; // Wrong gem type

    socket.gem = gem;
    return true;
  }

  public removeGem(instanceId: string, socketIndex: number): Gem | null {
    const sockets = this.itemSockets.get(instanceId);
    if (!sockets || socketIndex >= sockets.length) return null;

    const socket = sockets[socketIndex];
    const gem = socket.gem ?? null;
    socket.gem = undefined;
    return gem;
  }

  public getTotalStats(instanceId: string): Gem['stats'] {
    const sockets = this.itemSockets.get(instanceId);
    if (!sockets) return {};

    const totals: Gem['stats'] = {};

    for (const socket of sockets) {
      if (socket.gem) {
        for (const [stat, value] of Object.entries(socket.gem.stats)) {
          if (value) {
            const key = stat as keyof Gem['stats'];
            totals[key] = (totals[key] ?? 0) + value;
          }
        }
      }
    }

    return totals;
  }

  public getGemDefinitions(): Gem[] {
    return [...GEM_DEFINITIONS];
  }

  public getGemById(gemId: string): Gem | undefined {
    return GEM_DEFINITIONS.find(g => g.id === gemId);
  }
}
